import { useCallback, useEffect, useState } from 'react';
import { ApiError, getStorageUsage, type StorageUsage } from '@/lib/api';

/**
 * The daily usage series and the two peaks PLN-5's band review reads (STO-7).
 *
 * **`useStorageSummary`'s sibling, deliberately not its passenger.** This one is
 * supplementary: an admin opens the Storage surface to see how much room is
 * left, and the series is the evidence behind a band conversation. So a failure
 * here degrades to a missing chart rather than an empty screen, which is the
 * line `FolderView` already holds for its `useFolders` call — a view that broke
 * because a decorative second request failed would be a regression on a screen
 * that otherwise worked.
 */

export type StorageUsageState =
  | { kind: 'loading' }
  /** An all-zero series is loaded: an agency that has read nothing is normal. */
  | { kind: 'loaded'; usage: StorageUsage }
  | { kind: 'error'; status: number };

export interface UseStorageUsageResult {
  state: StorageUsageState;
  retry: () => void;
}

export function useStorageUsage(): UseStorageUsageResult {
  const [state, setState] = useState<StorageUsageState>({ kind: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    getStorageUsage(controller.signal)
      .then((usage) => {
        setState({ kind: 'loaded', usage });
      })
      .catch((error: unknown) => {
        if (error instanceof Error && error.name === 'AbortError') return;
        setState({ kind: 'error', status: error instanceof ApiError ? error.status : 0 });
      });

    return () => {
      controller.abort();
    };
  }, [reloadToken]);

  const retry = useCallback(() => {
    setState({ kind: 'loading' });
    setReloadToken((current) => current + 1);
  }, []);

  return { state, retry };
}
